import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import AdminSidebar from './layout/AdminSidebar';
import AdminNavbar from './layout/AdminNavbar';
import { AdminBrandProvider } from '../contexts/AdminBrandContext';
import { usePermissions } from '../hooks/usePermissions';

export function AdminProtectedRoute() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainContentRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const { isAdminOrHigher, user } = usePermissions();

  // Redirect non-admin users
  useEffect(() => {
    if (user && !isAdminOrHigher()) {
      navigate('/portal', { replace: true });
    }
  }, [user, isAdminOrHigher, navigate]);

  // Set document title for admin users
  useEffect(() => {
    document.title = 'GlobalBridge Portal';
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Show loading while checking permissions
  if (!user) {
    return (
      <SignedIn>
        <div className="flex items-center justify-center h-screen bg-gray-100">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </SignedIn>
    );
  }

  // Don't render for non-admin users
  if (!isAdminOrHigher()) {
    return null;
  }

  return (
    <>
      <SignedIn>
        <AdminBrandProvider>
          <div className="flex h-screen bg-gray-100">
            <AdminSidebar isOpen={sidebarOpen} />

            {sidebarOpen && (
              <div
                className="fixed inset-0 z-[5] bg-black/40 lg:hidden"
                onClick={() => setSidebarOpen(false)}
                aria-hidden="true"
              />
            )}

            <div className="flex flex-col flex-1 overflow-hidden lg:pl-20">
              <AdminNavbar toggleSidebar={toggleSidebar} />
              {/* Positioned so absolutely-positioned descendants (sr-only captions) resolve against this scroller, not the document */}
              <main
                ref={mainContentRef}
                className="relative flex-1 overflow-y-auto overflow-x-hidden bg-gray-100 p-4 lg:p-6"
              >
                <Outlet />
              </main>
            </div>
          </div>
        </AdminBrandProvider>
      </SignedIn>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>
    </>
  );
}
